import { useCallback } from 'react'
import { supabase } from '@/lib/supabaseClient'
import { toast } from '@/hooks/use-toast'
import { resolveCandidateHeadline } from '@/lib/candidateHeadline'
import { fetchAll, inChunks } from '@/lib/fetchAllRows'


export interface PipelineAssociation {
  id: string
  job_id: string
  candidate_id: string
  current_stage_id: string | null
  pipeline_position: number | null
  candidate_name: string
  linkedin_url?: string | null
  phone?: string | null
  created_at: string
  entered_stage_at: string | null
  status: 'active' | 'rejected' | 'hired' | 'offer' | string
  whatsapp_template_sent_at: string | null
  ai_fit_score?: number | null
  is_favorite: boolean
  added_by?: string | null
  current_role?: string | null
  current_company?: string | null
  rejection_reason_id?: string | null
  rejected_by?: string | null
  rejected_at?: string | null
  rejection_notes?: string | null
  updated_at?: string | null
  offered_at?: string | null
  hired_at?: string | null
}

/**
 * Hook providing basic pipeline operations:
 * - fetchAssociationsForJob: returns associations with candidate names
 * - moveAssociationToStage: updates stage (position auto-assigned by trigger)
 * - createAssociationAndMove: creates association (if missing) and sets stage
 */
export function usePipelineActions() {
  /**
   * `throwOnError`: throw instead of toasting and returning [], so a caller can tell
   * "couldn't load" from "no candidates" (the pipeline's error state).
   */
  const fetchAssociationsForJob = useCallback(async (jobId: string, opts: { throwOnError?: boolean } = {}): Promise<PipelineAssociation[]> => {
    // 1) Load associations for job
    //    Paged: the database returns at most 1,000 rows per request, and a big job's
    //    pipeline can hold more.
    let associations: any[] | null = null
    let assocError: unknown = null
    try {
      associations = await fetchAll(() =>
        supabase
          .from('job_candidate_associations')
          .select('id, job_id, candidate_id, current_stage_id, pipeline_position, created_at, entered_stage_at, status, whatsapp_template_sent_at, ai_fit_score, is_favorite, added_by, rejection_reason_id, rejected_by, rejected_at, rejection_notes, auto_rejected_rule, rejection_email_scheduled_for, updated_at, offered_at, hired_at')
          .eq('job_id', jobId)
          .order('pipeline_position', { ascending: true, nullsFirst: false })
          .order('created_at', { ascending: false }),
      )
    } catch (e) {
      assocError = e
    }

    if (assocError) {
      console.error('Error fetching associations:', assocError)
      if (opts.throwOnError) throw assocError
      toast({
        title: 'Error',
        description: 'Failed to load pipeline candidates.',
        variant: 'destructive',
      })
      return []
    }

    if (!associations || associations.length === 0) return []

    const candidateIds = Array.from(new Set(associations.map(a => a.candidate_id)))

    // 2) Load candidate names/links from independent candidates table, in chunks so
    //    the request URL stays short however many candidates the job has.
    let candidates: any[] = []
    let candError: unknown = null
    try {
      candidates = await inChunks(candidateIds as string[], async (chunk) => {
        const { data, error } = await supabase
          .from('candidates')
          .select('id, candidate_name, linkedin_url, phone, current_job_title, role_current, company_current, bio')
          .in('id', chunk)
        if (error) throw error
        return data || []
      })
    } catch (e) {
      candError = e
    }

    if (candError) {
      console.error('Error fetching candidates:', candError)
      if (opts.throwOnError) throw candError
      toast({
        title: 'Error',
        description: 'Failed to load candidate details.',
        variant: 'destructive',
      })
      return []
    }

    const byId = new Map((candidates || []).map(c => [c.id, c]))

    // Work-history fallback for candidates whose profile title/employer is empty.
    const needsExperience = (candidates || [])
      .filter(c => !((c as any).current_job_title || (c as any).role_current) || !(c as any).company_current)
      .map(c => c.id)
    const expById = new Map<string, { job_title: string | null; company_name: string | null }>()
    if (needsExperience.length > 0) {
      const { data: expRows } = await supabase
        .from('candidate_work_experience')
        .select('candidate_id, job_title, company_name, is_current, start_date')
        .in('candidate_id', needsExperience)
        .order('is_current', { ascending: false })
        .order('start_date', { ascending: false, nullsFirst: false })
      for (const row of expRows || []) {
        if (!expById.has(row.candidate_id)) {
          expById.set(row.candidate_id, { job_title: row.job_title, company_name: row.company_name })
        }
      }
    }

    const result: PipelineAssociation[] = associations.map(a => {
      const c = byId.get(a.candidate_id)
      const headline = resolveCandidateHeadline(c as any, expById.get(a.candidate_id) ?? null)
      return {
        id: a.id,
        job_id: a.job_id,
        candidate_id: a.candidate_id,
        current_stage_id: a.current_stage_id,
        pipeline_position: a.pipeline_position,
        candidate_name: c?.candidate_name || 'Unnamed Candidate',
        linkedin_url: c?.linkedin_url ?? null,
        phone: c?.phone ?? null,
        created_at: a.created_at,
        entered_stage_at: a.entered_stage_at ?? null,
        status: (a as any).status ?? 'active',
        whatsapp_template_sent_at: (a as any).whatsapp_template_sent_at ?? null,
        ai_fit_score: (a as any).ai_fit_score ?? null,
        is_favorite: (a as any).is_favorite ?? false,
        added_by: (a as any).added_by ?? null,
        rejection_reason_id: (a as any).rejection_reason_id ?? null,
        rejected_by: (a as any).rejected_by ?? null,
        rejected_at: (a as any).rejected_at ?? null,
        rejection_notes: (a as any).rejection_notes ?? null,
        updated_at: (a as any).updated_at ?? null,
        offered_at: (a as any).offered_at ?? null,
        hired_at: (a as any).hired_at ?? null,
        current_role: headline.role,
        current_company: headline.company,
      }
    })


    return result
  }, [])

  const moveAssociationToStage = useCallback(async (associationId: string, toStageId: string, options?: { silent?: boolean }) => {
    console.log('[usePipelineActions.moveAssociationToStage] moving', { associationId, toStageId })
    const { data, error } = await supabase
      .from('job_candidate_associations')
      .update({ 
        current_stage_id: toStageId, 
        pipeline_position: null,
        booking_link_sent_at: null,  // Reset for new stage
        entered_stage_at: new Date().toISOString()  // Update stage entry time
      })
      .eq('id', associationId)
      .select('id') // attempt to return updated row

    if (error) {
      console.error('Error moving candidate:', error)
      if (!options?.silent) {
        toast({
          title: 'Error',
          description: 'Failed to move candidate to selected stage.',
          variant: 'destructive',
        })
      }
      throw error
    }

    // If update succeeded but returned no rows (can happen if SELECT visibility is restricted by RLS),
    // verify via a follow-up read of the record.
    if (!data || data.length === 0) {
      console.warn('[usePipelineActions.moveAssociationToStage] update returned no rows; verifying via follow-up select', {
        associationId,
        toStageId,
      })
      const { data: verify, error: verifyError } = await supabase
        .from('job_candidate_associations')
        .select('id, current_stage_id')
        .eq('id', associationId)
        .maybeSingle()

      if (verifyError) {
        console.error('[usePipelineActions.moveAssociationToStage] verification select failed', verifyError)
        if (!options?.silent) {
          toast({
            title: 'Not moved',
            description:
              "The candidate may not have moved due to permissions or visibility. Please ensure you're assigned to this job and try again.",
            variant: 'destructive',
          })
        }
        throw new Error('Update may have failed: no rows visible after update')
      }

      if (verify && verify.current_stage_id === toStageId) {
        console.log('[usePipelineActions.moveAssociationToStage] verification shows candidate moved successfully')
        if (!options?.silent) {
          toast({
            title: 'Candidate moved',
            description: 'Candidate moved to the selected stage.',
          })
        }
        return
      }

      console.warn('[usePipelineActions.moveAssociationToStage] verification indicates candidate not moved', {
        verify,
      })
      if (!options?.silent) {
        toast({
          title: 'Not moved',
          description:
            "The candidate was not moved. You may not have permission, the stage/association may be invalid, or the hiring plan/stage isn't accessible.",
          variant: 'destructive',
        })
      }
      throw new Error('No rows updated when moving candidate')
    }

    if (!options?.silent) {
      toast({
        title: 'Candidate moved',
        description: 'Candidate moved to the selected stage.',
      })
    }
  }, [])

  const createAssociationAndMove = useCallback(async (jobId: string, candidateId: string, toStageId: string) => {
    console.log('[usePipelineActions.createAssociationAndMove] creating/moving', { jobId, candidateId, toStageId })
    // First check if association already exists
    const { data: existing, error: existingError } = await supabase
      .from('job_candidate_associations')
      .select('id')
      .eq('job_id', jobId)
      .eq('candidate_id', candidateId)
      .maybeSingle()

    if (existingError) {
      console.error('Error finding existing association:', existingError)
      toast({
        title: 'Error',
        description: 'Could not verify existing association.',
        variant: 'destructive',
      })
      throw existingError
    }

    if (existing?.id) {
      await moveAssociationToStage(existing.id, toStageId)
      return existing.id
    }

    // Create association and set stage
    const { data: created, error: createError } = await supabase
      .from('job_candidate_associations')
      .insert([{
        job_id: jobId,
        candidate_id: candidateId,
        current_stage_id: toStageId,
      }])
      .select('id') // ensure we get back the id if insert succeeded
      .single()

    if (createError) {
      console.error('Error creating association:', createError)
      toast({
        title: 'Error',
        description: 'Failed to add candidate to pipeline.',
        variant: 'destructive',
      })
      throw createError
    }

    if (!created?.id) {
      console.warn('[usePipelineActions.createAssociationAndMove] insert returned no id (RLS?)', { jobId, candidateId, toStageId })
      toast({
        title: 'Not added',
        description: 'Candidate was not added to the pipeline. You may not have permission for this job.',
        variant: 'destructive',
      })
      throw new Error('Association not created')
    }

    toast({
      title: 'Added to pipeline',
      description: 'Candidate added to the selected stage.',
    })

    return created.id
  }, [moveAssociationToStage])

  const updateAssociationStatus = useCallback(async (associationId: string, status: 'active' | 'rejected' | 'hired' | 'offer') => {
    console.log('[usePipelineActions.updateAssociationStatus] updating', { associationId, status })
    const { data, error } = await supabase
      .from('job_candidate_associations')
      .update({ status })
      .eq('id', associationId)
      .select('id')

    if (error) {
      console.error('Error updating status:', error)
      toast({ title: 'Error', description: 'Failed to update status.', variant: 'destructive' })
      throw error
    }

    if (!data || data.length === 0) {
      console.warn('[usePipelineActions.updateAssociationStatus] no rows updated (RLS or invalid id)', { associationId })
      toast({
        title: 'Not updated',
        description: 'The status was not changed. You may not have permission for this candidate/job.',
        variant: 'destructive',
      })
      throw new Error('No rows updated when changing status')
    }

    toast({ title: 'Status updated', description: `Candidate marked as ${status}.` })
  }, [])

  return {
    fetchAssociationsForJob,
    moveAssociationToStage,
    createAssociationAndMove,
    updateAssociationStatus,
  }
}
